<script lang="ts">
  import { type LoginSession } from '@nrk/innlogging-web/redirect-login-client';
  import * as Sentry from '@sentry/svelte';
  import { onDestroy, onMount } from 'svelte';
  import Router, {
    replace,
    type RouteDetail,
    type RouteLoadingEvent,
  } from 'svelte-spa-router';
  import { wrap } from 'svelte-spa-router/wrap';
  import AriaLive from './components/AriaLive.svelte';
  import Background from './components/Background.svelte';
  import CookieBanner from './components/CookieBanner.svelte';
  import InnloggingDebug from './components/Debug/InnloggingDebug.svelte';
  import FeedbackButton from './components/Feedback/FeedbackButton.svelte';
  import Footer from './components/Footer.svelte';
  import HeaderLink from './login/HeaderLink.svelte';
  import {
    forceRefresh,
    loginClient,
    loginSessionStore,
  } from './login/loginSessionStore';
  import ArtistPage from './pages/ArtistPage.svelte';
  import { readClientStateAriaLive } from './util/clientStateAriaLive';
  import { setUser, trackPageView } from './util/snowplow';

  import Home from './pages/Home.svelte';
  import vote from './stores/voteStore';
  import { clientState } from './util/stores';

  $: readClientStateAriaLive($clientState);

  const routes = {
    '/': Home,
    '/artist/:artistId': wrap({
      component: ArtistPage,
      conditions: (routeDetails: RouteDetail) => {
        if ($clientState?.type === 'after' || $clientState.type === 'poster') {
          return false;
        }
        if ($vote != null) {
          return false;
        }
        if (
          $clientState.alternatives.find(
            (alternative) => alternative.id === routeDetails?.params?.artistId
          ) != null
        ) {
          return true;
        }
        return false;
      },
    }),
  };

  onMount(async () => {
    const session = await loginClient.getSession();
    loginSessionStore.set(session);
    if (session.isLoggedIn && !!session.user) {
      setUser(session.user?.sub);
    }

    if (
      (session.isLoggedIn && session.isOffgrid) ||
      (session.isLoggedIn && session.isExpired)
    ) {
      forceRefresh();
    }

    trackPageView();
    loginClient.initOpSession();
    loginClient.addEventListener('update', onSessionUpdate);
  });

  onDestroy(async () => {
    loginClient.removeEventListener('update', onSessionUpdate);
  });

  const onSessionUpdate = (event: CustomEvent<LoginSession>) => {
    const session = event.detail;
    loginSessionStore.set(session);
    if (session.isLoggedIn && !!session.user) {
      setUser(session.user?.sub);
    }
  };

  const onRouteLoading = async (event: RouteLoadingEvent) => {
    const completePath = `${window.location.pathname.slice(0, -1)}${
      event.detail.location
    }`;
    Sentry.setContext('info', { ...event.detail, completePath });
  };

  const conditionsFailed = () => {
    replace('/');
  };
</script>

<InnloggingDebug />
<CookieBanner />
<HeaderLink />
<Background />
<main>
  <AriaLive mode="assertive" />
  <Router
    {routes}
    on:conditionsFailed={conditionsFailed}
    on:routeLoading={onRouteLoading}
  />
  <FeedbackButton />
</main>
<Footer />
{#await import('./components/Confetti.svelte') then c}
  <svelte:component this={c.default} />
{/await}

<style>
  main {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 0 var(--spacing-16) var(--spacing-32);
  }
</style>
